import { useAuth0 } from "@auth0/auth0-react";
import { Trans } from "@lingui/react/macro";
import {
  queryOptions,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createContext, useContext, useMemo } from "react";
import React from "react";

import { Failed as FailedComponent } from "@/common/components/info/info";
import { invariant } from "@/common/utils/invariant";
import {
  CompanySimpleUserTo,
  getCompanyUsers,
  getUserCompany,
} from "@/generated/api/users";
import { Language } from "@/generated/languages";
import { useLogout } from "@/routes/logout/logout";
import { countriesQuery } from "@/routes/shared/api/countries.api";
import { Currency } from "@/routes/shared/currency.params";

import { ErrorPage } from "../components/error-page/error-page";
import { LogoImage } from "../components/logo/logo";
import { useLocalStorage } from "../hooks/use-local-storage";
import { fallbackLanguage } from "../services/i18n";
import { getUser } from "../services/user";
import { sortString } from "../utils/array";

type Company = ReturnType<typeof selectCompany> & { users: Array<CompanyUser> };
type CompanyUser = ReturnType<typeof selectCompanyUser>;

type Context = {
  country: {
    code: string;
    currency: Currency;
  };
  companies: Array<Company>;
  company?: Company;
  setActiveCompany: (company: Company) => void;
  user: {
    id: string;
    hasManyCompanies: boolean;
    phone: {
      phoneNumber?: string;
      countryCode?: string;
    };
    email: string;
    language: Language;
    newsLanguage: Language;
    name: string;
  };
};

/**
 * Ireland is the only country where it is possible to use the platform without being a member of a company.
 */
const irishFallback: Context["country"] = {
  code: "IE",
  currency: "EUR",
};

const CompanyContext = createContext<Context | null>(null);

const CompanyProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useUser();
  const companies = useCompanies();
  const defaultCompany = companies.at(0);
  const [activeCompanyId, setActiveCompanyId, deleteActiveCompanyId] =
    useLocalStorage("company", defaultCompany?.id);
  const countries = useSuspenseQuery(countriesQuery());
  const activeCompany = companies.find(
    (company) => company.id === activeCompanyId,
  );
  const countryCode = activeCompany?.country ?? irishFallback.code;
  const currency =
    countries.data.find(
      (country) => country.id.toLowerCase() === countryCode.toLowerCase(),
    )?.currency.type ?? irishFallback.currency;

  const handleCompanyChange = React.useCallback(
    (company: Company) => {
      setActiveCompanyId(company.id);
    },
    [setActiveCompanyId],
  );

  React.useEffect(() => {
    if (companies.find((c) => c.id === activeCompanyId)) {
      return;
    }
    if (!defaultCompany) {
      deleteActiveCompanyId();
    }
    setActiveCompanyId(defaultCompany?.id);
  }, [
    activeCompanyId,
    companies,
    defaultCompany,
    deleteActiveCompanyId,
    setActiveCompanyId,
  ]);

  const value: Context = useMemo(
    () => ({
      companies,
      company: activeCompany,
      country: {
        code: countryCode,
        currency,
      },
      setActiveCompany: handleCompanyChange,
      user: {
        companies,
        email: user.email,
        hasManyCompanies: companies.length > 1,
        id: user.id,
        language: (user.user_metadata.can.settings?.language ??
          fallbackLanguage) as Language,
        name: user.name,
        newsLanguage: (user.user_metadata.can.settings?.news?.language ??
          fallbackLanguage) as Language,
        phone: {
          countryCode: user.user_metadata.common.phone.countryCode,
          phoneNumber: user.user_metadata.common.phone.phoneNumber,
        },
      },
    }),
    [
      activeCompany,
      countryCode,
      currency,
      handleCompanyChange,
      companies,
      user,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};

const TestCompanyProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <CompanyContext.Provider
      value={{
        companies: [],
        company: {
          approvalState: "APPROVED",
          country: "CZ",
          grants: [],
          id: 1,
          // eslint-disable-next-line lingui/no-unlocalized-strings
          name: "John Doe Company",
          number: "05215480",
          plan: {
            country: "CZ",
            id: 1,
            // eslint-disable-next-line lingui/no-unlocalized-strings
            name: "Basic",
            type: "BASIC",
          },
          role: "USER",
          users: [],
        },
        country: {
          code: "CZ",
          currency: "CZK",
        },
        setActiveCompany: () => {},
        user: {
          email: "user@example.local",
          hasManyCompanies: false,
          id: "user@example.local",
          language: "cs",
          // eslint-disable-next-line lingui/no-unlocalized-strings
          name: "John Doe",
          newsLanguage: "cs",
          phone: {},
        },
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

const useCompany = () => {
  const context = useContext(CompanyContext);
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(context, "useCompany must be used within CompanyProvider");

  return context;
};

const useUser = () => {
  const { user: _user } = useAuth0();
  const email = _user?.email;
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(_user, "User should be defined");
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(email, "User e-mail should be defined");

  const user = getUser(_user);

  return { ...user, id: _user.sub! };
};

const useCompanies = () => {
  const companies = useSuspenseQuery({
    ...companiesQuery(),
    select: (companies) =>
      companies
        .map(selectCompany)
        .toSorted((a, b) =>
          a.name && b.name ? sortString(a.name, b.name) : 0,
        ),
  });
  const users = useSuspenseQueries({
    queries: companies.data.map((company) =>
      companyUsersQuery({ companyId: company.id }),
    ),
  });

  return companies.data.map((company, i) => ({
    ...company,
    users: users[i].data,
  }));
};

const Failed = ({ error }: { error: unknown }) => {
  const logout = useLogout();
  return (
    <ErrorPage.Container>
      <LogoImage />
      <FailedComponent error={error}>
        <button className="text-can-forest-teal underline" onClick={logout}>
          <Trans>Zkuste se odhlásit a začít znovu</Trans>
        </button>
      </FailedComponent>
    </ErrorPage.Container>
  );
};

const selectCompany = (company: CompanySimpleUserTo) => ({
  approvalState: company.approvalState,
  country: company.companyCountry,
  grants: company.grants,
  id: company.companyId,
  name: company.companyName,
  number: company.companyNumber,
  plan: company.companyPlan,
  role: company.role,
});

const selectCompanyUser = (user: CompanySimpleUserTo) => ({
  approvalState: user.approvalState,
  grants: user.grants,
  role: user.role,
});

const companiesQuery = () =>
  queryOptions({
    queryFn: ({ signal }) => getUserCompany({ signal }),
    queryKey: ["companies"],
  });

const companyUsersQuery = ({ companyId }: { companyId: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompanyUsers(companyId, undefined, { signal }),
    queryKey: ["company", companyId, "users"],
    select: (users) => users.map(selectCompanyUser),
  });

CompanyProvider.Failed = Failed;

export { CompanyProvider, TestCompanyProvider, useCompany };
export type { Company };
